import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiAlertTriangle } from "react-icons/fi";
import {
  getDashboardStatusColor,
  getDashboardStatusColorHex,
  getDashboardStatusLabel,
} from "../../../constants/integrationTypes";
import CustomTooltip from "../../../components/common/CustomTooltip";
import TruncatedText from "../../../components/common/TruncatedText";
import { getPriorityColorClasses } from "../../../utils/taskUtils";
import Progress from "../../../pages/Projects/sections/Project/sections/Progress";
import { formatDate } from "../../../helpers/data-format";
import "./CadenceTable.css";
import {
  getChildEntityBreakdown,
  getChildItemCount,
  getEntityTypeDisplay,
  pluralizeEntityType,
} from "../../../utils/entityDisplayUtils";
import { EntityCountDisplay } from "../../../pages/Projects/sections/Project/sections/EntityCountDisplay";
import { Chip } from "@mui/material";
import RiskStatusDropdown from "../../../components/common/RiskStatusDropdown";
import DashboardStatusBadge from "../../../components/common/DashboardStatusBadge";
import { getRiskDetailsFromStats } from "../../../helpers/riskStatusHelper";
import { HiSparkles } from "react-icons/hi";
import { PinIcon, PinOff } from "lucide-react";
import { useOrganizationService } from "../../../services";
import store, { ReduxStateType } from "../../../redux/store";
import { clearWorkItemsData } from "../../../redux/slices/organization";
import TooltipWrapper from "../../atoms/TooltipWrapper";

// Status Column
export const StatusCell = ({ row }: { row: any }) => {
  const statusRaw = row.status || "";
  const statusLower = String(statusRaw).toLowerCase();
  const isRiskContributing =
    statusLower === "delayed" ||
    statusLower === "unallocated" ||
    statusLower === "blocked";
  const dotClass = getDashboardStatusColor(statusRaw);
  const label = getDashboardStatusLabel(statusRaw);

  const entityInfo = getEntityTypeDisplay(
    row?.entity_type ?? "strategy_map",
    row.type,
  );

  return (
    <>
      {row.status === "at_risk" ? (
        <RiskStatusDropdown
          entityName={row.name || "Untitled Project"}
          entityType={entityInfo.aiEntityType as any}
          entityId={row.id}
          riskDetails={getRiskDetailsFromStats(
            row.stats,
            getChildEntityBreakdown(row.child_entity_types)[0]?.type ||
              "work item",
          )}
          status={row.status}
        >
          <span
            className={`ct-badge  ${" border-red-500/20 hover:border-red-400/40 transform hover:scale-105 cursor-pointer transition-all duration-200 relative font-medium after:absolute after:-inset-0 after:rounded-lg after:border after:border-red-400/40 after:opacity-0 group-hover:after:opacity-100 group-hover:after:animate-[ping_1s_ease-in-out_2] after:pointer-events-none after:z-[-1]"}  `}
          >
            <span
              className={`w-[10px] h-[10px] rounded-full ${getDashboardStatusColor(row.status || "")}`}
            ></span>
            {/* {getDashboardStatusLabel(row.status || "")} */}

            <div className={`flex items-center gap-1 relative `}>
              <span
                className={`inline-flex items-center gap-1 rounded-lg whitespace-nowrap dark:text-white text-black `}
              >
                {getDashboardStatusLabel(row.status || "")}
              </span>
              {
                <HiSparkles
                  title="AI Help"
                  className="w-4 h-4 text-blue-500 hover:text-purple-500 cursor-pointer transition-all duration-200 drop-shadow-sm"
                />
              }
            </div>
          </span>
        </RiskStatusDropdown>
      ) : (
        <span className={`ct-badge `}>
          <span
            className={`w-[10px] h-[10px] rounded-full ${getDashboardStatusColor(row.status || "")}`}
          ></span>
          {getDashboardStatusLabel(row.status || "")}
        </span>
      )}
    </>
  );
};

// Task Type Column
export const TaskTypeCell = ({ type }: { type: string }) => {
  return <>{type}</>;
};

// Title Column (Truncated)
export const TitleCell = ({
  row,
  isHovered,
}: {
  row: any;
  isHovered?: boolean;
}) => {
  const [isPinned, setIsPinned] = useState(row.pinned);
  const { pinWorkItem, fetchRoadmapsAndProjects } = useOrganizationService();
  const dispatch = useDispatch();

  const { pinnedProjectsIds } = useSelector(
    (state: ReduxStateType) => state.organization,
  );

  const isPinnedByApi = pinnedProjectsIds.includes(row.id);

  const entityInfo = getEntityTypeDisplay(
    row?.entity_type ?? "strategy_map",
    row.type,
  );

  const IconComponent = entityInfo.icon;

  const handlePinClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const newPinnedStatus = !isPinned;

    // Optimistic update locally
    setIsPinned(newPinnedStatus);

    // Dispatch event immediately for other components
    window.dispatchEvent(
      new CustomEvent("workitem-pinned-changed", {
        detail: { id: row.id, pinned: newPinnedStatus },
      }),
    );

    try {
      await pinWorkItem({
        id: row.id,
        type: row.type || row.entity_type || "project",
        pin: newPinnedStatus,
      });

      // Refetch the work items list to reflect pin/unpin changes
      const {
        workItemsSearchTerm = "",
        workItemsSearchType = "all",
        workItemsSortBy = "modified_at_desc",
        workItemsSourceFilter = "",
        workItemsQuarterFilter = [],
        workItemsPinnedFilter = false,
      } = store.getState().organization;

      dispatch(clearWorkItemsData());
      fetchRoadmapsAndProjects(
        true,
        undefined,
        workItemsSearchTerm,
        workItemsSearchType,
        workItemsSortBy,
        workItemsSourceFilter,
        workItemsQuarterFilter,
        workItemsPinnedFilter,
      );
    } catch (error) {
      console.error("Failed to pin item:", error);
      // Rollback on error
      const rolledBackStatus = !newPinnedStatus;
      setIsPinned(rolledBackStatus);
      window.dispatchEvent(
        new CustomEvent("workitem-pinned-changed", {
          detail: { id: row.id, pinned: rolledBackStatus },
        }),
      );
    }
  };

  useEffect(() => {
    const handlePinChange = (event: Event) => {
      const { id, pinned } = (event as CustomEvent).detail;
      if (id === row.id) {
        setIsPinned(pinned);
      }
    };

    window.addEventListener("workitem-pinned-changed", handlePinChange);
    return () => {
      window.removeEventListener("workitem-pinned-changed", handlePinChange);
    };
  }, [row.id]);

  useEffect(() => {
    setIsPinned(isPinnedByApi);
  }, [isPinnedByApi]);

  return (
    <div className="flex items-center justify-start min-w-0 w-full gap-2 relative overflow-visible">
      {/* New badge - green pill, shown when task is new in sim */}
      {row.sim_flag === true && !row.isUpdated && (
        <div className="left-[8px] top-[-14px] px-2 py-1 absolute text-center text-white text-[10px] font-normal font-['Inter'] leading-none bg-green-400 rounded-lg z-[10]">
          New
        </div>
      )}
      {/* Updated badge - indigo pill, shown when task is updated in sim */}
      {row.isUpdated === true && (
        <div className="left-[8px] top-[-14px] px-2 py-1 absolute text-center text-white text-[10px] font-normal font-['Inter'] leading-none bg-indigo-400 rounded-lg z-[10]">
          Updated
        </div>
      )}
      <div className="flex p-2 relative">
        <IconComponent
          className={`w-[20px] h-[20px] table-entity-icon ${entityInfo.iconClass}`}
        />

        {isPinned ? (
          <span className="table-pin-icon" onClick={handlePinClick}>
            <TooltipWrapper message="Unpin">
              <PinOff className={`w-[20px] h-[20px] ${entityInfo.iconClass}`} />
            </TooltipWrapper>
          </span>
        ) : (
          <span className="table-pin-icon" onClick={handlePinClick}>
            <TooltipWrapper message="Pin">
              <PinIcon
                className={`w-[20px] h-[20px] ${entityInfo.iconClass}`}
              />
            </TooltipWrapper>
          </span>
        )}
      </div>
      <div className="flex flex-col min-w-0 flex-1 overflow-hidden">
        <div className="flex gap-2 min-w-0">
          <TruncatedText
            text={row.name ?? row.project_name}
            className="w-full truncate flex-wrap"
            placement="top-start"
          />
        </div>

        <div className="flex ">
          <div className=" min-w-0 flex items-center overflow-hidden gap-1 w-fit">
            <span
              className={`text-[12px] uppercase ${entityInfo.labelClass} !w-fit flex items-center gap-1 `}
            >
              {isPinned ? <PinIcon className="w-[12px] h-[12px]" /> : ""}
              {entityInfo.label}
              {row.integration_data?.integration_type
                ? ` (${row.integration_data?.integration_type})`
                : ""}

              {/* {row.stage ? ` - ${row.stage}` : ""} */}

              {/* {row.type ? <span className=""> ({row.type})</span> : ""} */}
            </span>
          </div>

          {getChildItemCount(row) > 0 && (
            <EntityCountDisplay
              count={getChildItemCount(row)}
              childEntityTypes={row.child_entity_types}
              entityType={row.entity_type}
              className="text-[10px]"
              showSeparator={true}
              textSize="10px"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const ViewTitleSearchValue = (row: any) => {
  const entityInfo = getEntityTypeDisplay(
    row?.entity_type ?? "strategy_map",
    row.type,
  );

  // Core text: Name
  const name = row.name ?? row.project_name ?? "";

  // Label
  const label = entityInfo.label;

  // Integration type
  const integrationType = row.integration_data?.integration_type
    ? `(${row.integration_data?.integration_type})`
    : "";

  // Child count text (e.g. "5 Epics")
  let childCountText = "";
  const count = getChildItemCount(row);
  if (count > 0) {
    const { sublabel } = getEntityTypeDisplay(row.entity_type, row.type);
    childCountText = `${count} ${pluralizeEntityType(count, sublabel)}`;
  }

  return `${name} ${label} ${integrationType} ${childCountText}`;
};

// Integration Type Column
export const IntegrationTypeCell = ({ type }: { type?: string }) => {
  return type ? (
    <span className="ct-badge ct-badge-purple !px-2.5 !py-0.5 text-xs">
      {type}
    </span>
  ) : (
    <span>-</span>
  );
};

// Integration Status Column
export const IntegrationStatusCell = ({ status }: { status?: string }) => {
  return status ? (
    <span className="capitalize text-xs flex font-medium px-2.5 py-1">
      {status}
    </span>
  ) : (
    <span>-</span>
  );
};

// Generic Story Points / Estimate Cell
export const EstimateCell = ({
  value,
  isComputed,
  isCompleted = false,
  tooltipTitle,
  showHyphenIfNull = true,
}: {
  value?: string | number | null;
  isComputed?: boolean;
  isCompleted?: boolean;
  tooltipTitle?: string;
  showHyphenIfNull?: boolean;
}) => {
  if (value !== undefined && value !== null) {
    return (
      <span
        className={`inline-flex items-center gap-1 font-medium px-2 py-0.5 text-s rounded-md bg-gray-100 dark:bg-gray-800 w-fit ${isCompleted ? "text-green-600 dark:text-green-400" : "text-gray-700 dark:text-gray-300"}`}
      >
        {value}
        {isComputed && (
          <CustomTooltip title={tooltipTitle || "Computed by Cadence Pro"}>
            <span className="text-yellow-500 text-s cursor-help">★</span>
          </CustomTooltip>
        )}
      </span>
    );
  }
  return showHyphenIfNull ? (
    <span className="text-s text-gray-400 px-2">-</span>
  ) : null;
};

// Progress Column
export const ProgressCell = ({ progress }: { progress: number }) => {
  return <Progress progress={progress} />;
};

// Delayed / Duration Column
export const DurationCell = ({ value }: { value: string | number }) => {
  return <span className="ct-delayed-cell">{value}</span>;
};

// Due Date / Start Date Column
export const DateCell = ({ date }: { date?: string }) => {
  return <>{date ? formatDate(date) : "Not set"}</>;
};

// Priority Column
export const PriorityCell = (
  props: { priority?: string | number } & React.HTMLAttributes<HTMLSpanElement>,
) => {
  const { priority } = props;
  if (!priority) {
    return (
      <span className="ct-text-cell ct-text-cell-padded ct-badge-gray">-</span>
    );
  }
  const orgSetting = store.getState().organization.orgDetails;

  const renderPriority = useMemo(() => {
    if (orgSetting?.priorities) {
      const priorityData = orgSetting.priorities.find(
        (p: any) => p.id === priority,
      );

      if (!priorityData) return <></>;

      return (
        <>
          <span
            {...props}
            className={`ct-priority-badge uppercase ${props?.className}`}
            style={{
              backgroundColor: priorityData?.color,
              color: getReadableColor(priorityData?.color),
              ...props?.style,
            }}
          >
            {priorityData?.id}
          </span>
        </>
      );
    }
    return <span className={`ct-priority-badge`}>--</span>;
  }, [props]);
  return renderPriority;
};

// Skill Column
export const SkillCell = ({ skill }: { skill?: string }) => {
  return <>{skill}</>;
};

// Dependency Column
export const DependencyCell = ({
  dependencies,
}: {
  dependencies?: string[];
}) => {
  const deps =
    Array.isArray(dependencies) && dependencies.length > 0
      ? dependencies.join(", ")
      : "-";

  if (deps === "-") {
    return <span>-</span>;
  }

  const maxLength = 40;
  const truncated =
    deps.length > maxLength ? deps.substring(0, maxLength) + "..." : deps;

  return (
    <CustomTooltip title={deps}>
      <span className="truncate block cursor-pointer">{truncated}</span>
    </CustomTooltip>
  );
};

// ID Column
export const IdCell = ({ id }: { id: string | number }) => {
  return <div className="ct-id-cell">{id}</div>;
};

function adjustColor(color: string, amount: number) {
  let col = color.replace("#", "");

  const num = parseInt(col, 16);

  let r = (num >> 16) + amount;
  let g = ((num >> 8) & 0x00ff) + amount;
  let b = (num & 0x0000ff) + amount;

  r = Math.max(Math.min(255, r), 0);
  g = Math.max(Math.min(255, g), 0);
  b = Math.max(Math.min(255, b), 0);

  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

function getReadableColor(bgColor?: string) {
  if (!bgColor) return "#000000";
  const color = bgColor.replace("#", "");

  const r = parseInt(color.substr(0, 2), 16);
  const g = parseInt(color.substr(2, 2), 16);
  const b = parseInt(color.substr(4, 2), 16);

  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  // light bg → darker text, dark bg → lighter text
  return brightness > 140
    ? adjustColor(bgColor, -120) // darker shade
    : adjustColor(bgColor, 120); // lighter shade
}